import { z } from "zod/v4";

export const pdfTemplateDesignSchema = z.object({
  // Absence is intentional: historical snapshots must retain inset rendering.
  pageVersion: z.union([z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)]).optional(),
  layout: z.enum(["classic", "architect", "bold"]),
  titleFont: z.enum(["helvetica", "poppins", "anton"]).optional(),
  companyNameFont: z.literal("title").optional(),
  companyNameSize: z.number().int().min(8).max(24).optional(),
  artworkStyle: z.enum(["modern", "luxury", "minimalist"]).optional(),
  textColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  accentColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  coverEnabled: z.boolean(),
  thankYouEnabled: z.boolean(),
  useInteriorBranding: z.boolean(),
  hideLogoBackground: z.boolean().optional(),
  removeLogoBackground: z.boolean().optional(),
  logoAlignment: z.enum(["left", "center"]).optional(),
  showCompanyName: z.boolean().optional(),
  coverTitle: z.string().trim().min(1).max(90),
  coverSubtitle: z.string().trim().max(240),
  thankYouTitle: z.string().trim().min(1).max(90),
  thankYouMessage: z.string().trim().max(900),
  logoUrl: z.string().max(500).nullable(),
  coverArtworkUrl: z.string().max(500).nullable(),
  thankYouArtworkUrl: z.string().max(500).nullable(),
}).strict();

export type PdfTemplateDesign = z.infer<typeof pdfTemplateDesignSchema>;
export interface PdfTemplateCompany {
  name: string;
  address?: string;
  email?: string;
  phone?: string;
  website?: string;
  vatNumber?: string;
  logoUrl?: string;
  showCompanyName?: boolean;
  logoSize?: "small" | "medium" | "large";
}
export interface PdfTemplateSnapshot {
  version: string;
  design: PdfTemplateDesign;
  company: PdfTemplateCompany;
}

export function readPdfTemplateSnapshot(value: unknown): PdfTemplateSnapshot | null {
  const parsed = z.object({
    version: z.string().min(1).max(100),
    design: pdfTemplateDesignSchema,
    company: z.object({
      name: z.string().max(250), address: z.string().max(2000).optional(),
      email: z.string().max(250).optional(), phone: z.string().max(100).optional(),
      website: z.string().max(500).optional(), vatNumber: z.string().max(100).optional(),
      logoUrl: z.string().max(500).optional(), showCompanyName: z.boolean().optional(),
      logoSize: z.enum(["small", "medium", "large"]).optional(),
    }),
  }).safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function defaultPdfTemplate(brandColor = "#374349", logoUrl: string | null = null): PdfTemplateDesign {
  return {
    pageVersion: 6, companyNameFont: "title", companyNameSize: 16,
    layout: "classic", primaryColor: /^#[0-9a-f]{6}$/i.test(brandColor) ? brandColor : "#374349",
    accentColor: "#5aabbf", coverEnabled: true, thankYouEnabled: true, useInteriorBranding: true,
    coverTitle: "Your project quote", coverSubtitle: "Prepared with care. Built around your project.",
    thankYouTitle: "Thank you", thankYouMessage: "Thank you for considering us for your project. If you have any questions about this quote, please get in touch. We look forward to working with you.",
    logoAlignment: "left",
    logoUrl, coverArtworkUrl: null, thankYouArtworkUrl: null,
  };
}

// URLs contain only opaque asset ids. Public quote access rewrites these to
// capability-bound routes, never publicly exposes the private storage bucket.
export function templateAssetId(url: string | null | undefined): string | null {
  return url?.match(/^\/api\/pdf-template\/assets\/([0-9a-f-]{36})$/i)?.[1] ?? null;
}
export function templateAssetIds(snapshot: PdfTemplateSnapshot): string[] {
  return [snapshot.design.logoUrl, snapshot.design.coverArtworkUrl, snapshot.design.thankYouArtworkUrl]
    .map(templateAssetId).filter((id): id is string => Boolean(id));
}
export function publicPdfTemplate(snapshot: PdfTemplateSnapshot | null | undefined, token: string): PdfTemplateSnapshot | null {
  if (!snapshot) return null;
  const asset = (url: string | null) => {
    const id = templateAssetId(url);
    return id ? `/api/public/quote/${encodeURIComponent(token)}/template-assets/${id}` : null;
  };
  const design = { ...snapshot.design, logoUrl: asset(snapshot.design.logoUrl),
    coverArtworkUrl: asset(snapshot.design.coverArtworkUrl), thankYouArtworkUrl: asset(snapshot.design.thankYouArtworkUrl) };
  return { ...snapshot, design, company: { ...snapshot.company, logoUrl: design.logoUrl ?? undefined } };
}
